import { useState, useEffect, useMemo } from 'react';
import { Edit, Trash, Plus } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { DataTable } from '../../components/ui/data-table';
import { storage } from '../../lib/storage';
import { Subject } from '../../types/common';
import { useToast } from '../../hooks/use-toast';
import { app } from "../../firebase";
import { getAuth } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocs,
  getFirestore,
  onSnapshot,
  setDoc,
  Timestamp,
  updateDoc,
} from "firebase/firestore";

const Subjects = () => {
  const { toast } = useToast();
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [filteredSubjects, setFilteredSubjects] = useState<Subject[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [selectedSubject, setSelectedSubject] = useState<any>({});
  const [editingSubjectId, setEditingSubjectId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [allSubjects, setAllSubjects] = useState([]);
  const db = getFirestore(app);
  const auth = getAuth(app);

  useEffect(() => {
    const certificatesUnsubscribe = onSnapshot(
      collection(db, "allSubjects"),
      (querySnapshot) => {
        //   setLoader(true); // Show loader while fetching data
        const result = querySnapshot.docs.map((doc) => ({
          id: doc.id, // Get the document ID  
          ...doc.data(), // Spread the document data  
        }));
        setAllSubjects(result);
        //   setLoader(false); // Hide loader after fetching data
      },
      (error) => {
        //  addToast(error.message, "error"); // Handle errors
        //  setLoader(false); // Hide loader in case of error
      }
    );
    return () => {
      certificatesUnsubscribe();
    };
  }, [])


  const handleAddSubject = () => {
    setSelectedSubject("");
    setEditingSubjectId(null);
    setIsDialogOpen(true);
  };



  const handleSaveSubject = async () => {
    if (!selectedSubject.trim()) {
      toast({
        title: "Error",
        description: "Subject name cannot be empty.",
        variant: "destructive"
      });
      return;
    }

    // Check for duplicate names  
    const isDuplicate = subjects.some(
      s => s.subjectName.toLowerCase() ===
        selectedSubject.toLowerCase() && s.id !== editingSubjectId
    );

    if (isDuplicate) {
      toast({
        title: "Error",
        description: "A subject with this name already exists.",
        variant: "destructive"
      });
      return;
    }

    if (selectedSubject && editingSubjectId) {
      // Update existing subject in Firestore  
      const updatedSubject: Subject = {
        id: editingSubjectId,
        className: selectedSubject,
        subjectName: selectedSubject
      };

      const subjectRef = doc(db, "allSubjects", editingSubjectId); // Get a reference to the course document  
      // Use setDoc to update the document in Firestore  
      await setDoc(subjectRef, {...updatedSubject}); // Update the course data in Firestore  

      toast({
        title: "Subject Updated",
        description: "The subject has been updated successfully.",
        className: "bg-bytesplash-blue text-white",
      });
    } else {


      const subjectRef = doc(collection(db, "allSubjects"));

      // Add the generated ID to the course data object  
      const newSubjectRefDataWithId = {
        id: subjectRef.id, // Assign the generated ID  
        subjectName: selectedSubject,  // Spread the existing data 
        className: selectedSubject
      };

      // Save the course data to Firestore with the generated ID  
      await setDoc(subjectRef, newSubjectRefDataWithId);
      setSubjects((prev) => ([...prev, newSubjectRefDataWithId]));
      //
      toast({
        title: "Subject Added",
        description: "The new subject has been added successfully.",
        className: "bg-bytesplash-blue text-white",
      });
    }

    // Close dialog and reload subjects  
    setIsDialogOpen(false);
    setSelectedSubject(null); // Reset selected subject after save 
  };

  const handleEditSubject = (subject: Subject) => {
    setEditingSubjectId(subject?.id);
    setSelectedSubject(subject?.subjectName ?? subject?.className); // Update selected subject state   
    setIsDialogOpen(true); // Open dialog for editing  
  };

  const handleDeleteSubject = async (id: string) => {
    try {
      const subjectRef = doc(db, "allSubjects", id);
      await deleteDoc(subjectRef); // Firestore delete operation  

      setSubjects((prevSubjects) =>
        prevSubjects.filter((subject) => subject.id !== id)
      );

      toast({
        title: "Subject Deleted",
        description: "The subject has been removed successfully.",
        className: "bg-bytesplash-blue text-white",
      });
    } catch (error) {
      console.error("Error deleting subject: ", error);
      toast({
        title: "Error",
        description: "Failed to delete the subject.",
        variant: "destructive",
      });
    }
  };

  const columns: any = [
    {
      id: "id",
      header: "Subject ID",
      accessorKey: "id",
      enableSorting: true
    },
    {
      id: "className",
      header: "Subject Name",
      accessorKey: "className",
      enableSorting: true
    },
    {
      id: "actions",
      header: "Edit Subject",
      enableSorting: false,
      cell: ({ row }) => {
        const data = row.original;
        return (
          <div className="flex justify-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleEditSubject(data)}
              className="h-8 w-8"
            >
              <Edit className="h-4 w-4 text-bytesplash-blue" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDeleteSubject(data.id)}
              className="h-8 w-8"
            >
              <Trash className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        )
      }
    }
  ];
  const filteredSubjectsObj = useMemo(() => {
    return allSubjects?.filter((subject) => {
      const lowerCaseQuery = searchQuery?.toLowerCase();
      return (
        subject?.className?.toLowerCase()?.includes(lowerCaseQuery) || // Check className  
        subject?.subjectName?.toLowerCase()?.includes(lowerCaseQuery) ||   // Check subjectName  
        subject?.id?.toLowerCase()?.includes(lowerCaseQuery)         // Check id  
      );
    });
  }, [searchQuery, allSubjects]);

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-bytesplash-blue">Subjects</h1>
        <Button
          className="bg-bytesplash-blue hover:bg-blue-700"
          onClick={handleAddSubject}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add New Subject
        </Button>
      </div>

      <div className="mb-6">
        <Input
          type="text"
          placeholder="Search subjects..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)} // Update search query on input change  
          className="max-w-sm border-2 hover:border-bytesplash-blue"
        />
      </div>

      <Card className="shadow-md">
        <CardContent className="p-4">
          <DataTable
            columns={columns}
            data={filteredSubjectsObj} // Use filtered subjects for displaying in the table  
            noResultsMessage="No subjects found"
          />
        </CardContent>
      </Card>

      {/* Add/Edit Subject Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingSubjectId ? "Edit Subject" : "Add Subject"}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {editingSubjectId && (
              <div className="grid grid-cols-4 items-center gap-4">
                <Label className="text-right text-gray-600">Subject ID</Label>
                <div className="col-span-3">
                  <p className="text-sm text-gray-500 font-medium">{editingSubjectId}</p>
                </div>
              </div>
            )}

            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="subjectName" className="text-right">Subject Name</Label>
              <Input
                id="subjectName"
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                className="col-span-3 border-2 hover:border-bytesplash-blue"
              />
            </div>
          </div>

          <DialogFooter className="sm:justify-between">
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSubject}
              className="bg-bytesplash-blue hover:bg-blue-700"
            >
              {editingSubjectId ? "Update Subject" : "Add Subject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Subjects;  